import { datadogLogs } from '@datadog/browser-logs'
import type { Bet } from 'src/types/domain/Bet'
import type { BetActions } from 'src/store/bet'
import { useBetStore } from 'src/store/bet'
import type { PlayerActions } from 'src/store/player'
import type { StreakActions } from 'src/store/streak'
import { betStatusMap } from 'src/types/domain/BetStatus'
import type { RaceSettledEvent } from 'src/types/domain/RaceEvent'
import { emitBetResult } from 'src/services/postMessage'
import { useGameStore } from 'src/store/game'
import { getBetStatusByRaceSettled } from './betUtils'
import { isUnregisteredAtSettlement } from './betSettlement'
import { isCrashGameType } from './gameTypeUtils'

/** The subset of widget actions settlement needs. The full `Actions` object
 *  (websocketEventHandlers) is structurally assignable to this. */
interface SettleActions {
  bet: BetActions
  player: PlayerActions
  streak?: StreakActions
}

/**
 * Settle the local bets for a race that just emitted `race_settled`.
 *
 * Called from `handleRaceEventCreated`. Extracted so the reconcile + balance
 * behaviour below is unit-testable without a full Supabase-event fixture.
 *
 * `updateBalance` (optional) re-reads the operator-authoritative balance. It is
 * only invoked when a bet is refunded via the never-acknowledged path — see the
 * note at the bottom of this function.
 *
 * `fetchServerBets` (optional) returns the authoritative server-side bet list
 * (see `fetchServerBetsSnapshot`). Injected so it can be stubbed in tests and
 * so the socket module graph stays out of this util. When omitted, settlement
 * behaves exactly as before (purge unacknowledged bets without reconciling).
 */
export const settleBetsForRace = async (
  actions: SettleActions,
  newRaceEvent: RaceSettledEvent,
  updateBalance?: () => Promise<void>,
  fetchServerBets?: () => Promise<Bet[] | undefined>
): Promise<void> => {
  // ── C1+C2 — authoritative reconcile BEFORE any purge ────────────────────
  // A real bet — win or lose — leaves `unacceptedBets` at PLACEMENT (its
  // `bet:accepted`). But on a slow device that broadcast can be dropped, so a
  // bet that WAS registered still sits in `unacceptedBets` at settlement. The
  // old code purged every such bet — it vanished from BOTH the chip panel and
  // the bet history, and only a manual refresh's `bets:get` brought it back
  // (Kasper's "placed 4 bets → most disappear at settle, all return on
  // refresh"). Here we fetch the authoritative server list first and HEAL any
  // unaccepted bet the server actually holds, so only genuine phantoms (server
  // confirms absent) are purged below.
  //
  // Only fetch when there is something to reconcile — the common case (all
  // bets acknowledged) skips the fetch entirely and settles synchronously, so
  // normal-round timing is unchanged. If the fetch is unavailable or fails we
  // fall back to the previous purge behaviour (safe: the stake is refunded and
  // the balance re-synced below, and the next (re)join's `bets:get` restores
  // anything that was actually registered).
  const localUnacceptedForRace = actions.bet
    .getBetsByRaceId(newRaceEvent.raceId)
    .filter((bet) => isUnregisteredAtSettlement(bet, useBetStore.getState().unacceptedBets))

  // Only reconcile on the surface that wires the full settlement machinery —
  // i.e. where `updateBalance` is present (the game route). The lobby mounts
  // this handler for EVERY game with no `updateBalance`, so gating on it keeps
  // the authoritative bets:get off the lobby channel (it has no chip panel to
  // heal and no balance reconciler) and leaves the lobby's settle behaviour
  // exactly as it was before this reconcile existed.
  if (localUnacceptedForRace.length > 0 && fetchServerBets && updateBalance) {
    try {
      const serverBets = await fetchServerBets()
      if (serverBets) {
        const serverById = new Map(serverBets.map((bet) => [bet.id, bet]))
        for (const localBet of localUnacceptedForRace) {
          const serverBet = serverById.get(localBet.id)
          if (!serverBet) continue
          // A bet:won / bet:rejected / bet:accepted for this bet may have
          // landed DURING the await — JS is single-threaded, but the event
          // loop dispatched other channel frames while the bets:get RTT was
          // pending. If so, DON'T heal: betAccepted does an unconditional
          // overwrite that would reset a just-WON status back to the
          // (read-model-lagged) PENDING snapshot, defeating player.betWon's
          // status-based idempotency guard (→ double-credit of the local
          // balance) and clobbering the DDB-authoritative boosted
          // potentialPayout. Only heal bets still genuinely pending-unaccepted.
          const fresh = useBetStore.getState()
          const stillPendingUnaccepted =
            fresh.unacceptedBets[localBet.id] !== undefined &&
            fresh.bets.byId[localBet.id]?.status === betStatusMap.PENDING
          if (!stillPendingUnaccepted) continue
          // Registered server-side; only the `bet:accepted` ack was lost.
          // Heal it (removes from `unacceptedBets`, keeps it in the store) so
          // it settles as a normal won/lost bet below instead of vanishing.
          actions.bet.betAccepted(serverBet)
        }
      }
    } catch {
      // Reconcile fetch failed — fall through to the purge path below (safe:
      // stake refunded + balance re-synced; next (re)join's bets:get restores
      // anything actually registered). Never let it abort settlement.
    }
  }

  // Recompute after any healing — the healed bets are now normal store bets.
  const bets = actions.bet.getBetsByRaceId(newRaceEvent.raceId)
  const unacceptedBets = useBetStore.getState().unacceptedBets

  // CRASH GAMES settle by CASHOUT during the climbing phase, never by marble
  // position. The crash "race" still emits a cosmetic 2-marble result at settle,
  // so marble-matching a non-cashed crash bet (the WON/LOST branch below) would
  // mark it WON at potentialPayout whenever its assigned marble equals that fixed
  // winner — deterministic for slot-1 bets, i.e. a phantom "won 250k" that only
  // corrects on refresh (the money is never real; the Go backend settles these
  // LOST). Detect crash from the bet's own game and force every non-cashed crash
  // bet to LOST here, mirroring the server-side IsCrash() settlement guard.
  const gameState = useGameStore.getState()
  const raceGame = gameState.games.byId[bets[0]?.raceConfigurationId ?? '']
  const raceTrack = raceGame ? gameState.tracks.byId[raceGame.trackId] : undefined
  const isCrashRace = !!raceTrack && isCrashGameType(raceTrack.gameType)

  // Track if user had any winning or losing bets
  let hasWinningBet = false
  let hasLosingBet = false
  // Did we refund a bet that was never acknowledged by the server? If so the
  // optimistic balance is no longer trustworthy and must be reconciled below.
  let didRefundUnacceptedBet = false

  // Update bet states
  bets.forEach((bet) => {
    // Still unacknowledged AFTER the reconcile above ⇒ the server confirmed it
    // absent (or the fetch was unavailable) — a genuine phantom. Purge +
    // refund instead of settling, so it can't surface as a phantom "won $0" in
    // the chip-placement panel or the bet history, and the optimistically-
    // deducted stake is returned. Done before the won/lost marking below so it
    // never renders for a frame.
    if (isUnregisteredAtSettlement(bet, unacceptedBets)) {
      // C3 — surface the silent failure so its rate is measurable. This purge
      // preempts the chip panel's own reconcile, whose warn was dead code, so
      // this is the single authoritative place a never-registered bet is
      // recorded.
      datadogLogs.logger.warn('chip_placement_bet_silent_failure', {
        user_event: {
          betId: bet.id,
          raceId: bet.raceId,
          amount: bet.betAmount,
          optionId: bet.betDetails?.selectedItems?.[0],
          reason: 'unregistered_at_settlement'
        }
      })
      actions.bet.betUnaccepted(bet)
      actions.player.betUnaccepted(bet)
      didRefundUnacceptedBet = true
      return
    }

    // CRASH GAME: Skip bets that are already cashed_out
    // These bets were settled during the climbing phase, not by marble positions
    if (bet.status === 'cashed_out') {
      hasWinningBet = true // Treat cashout as a win for UI purposes
      return
    }

    // CRASH GAME: any bet that reaches here (not cashed out before the crash) is
    // a LOSS. Never marble-match it against the cosmetic race result — that is
    // what produced the phantom "won 250k" (see the isCrashRace note above).
    if (isCrashRace) {
      hasLosingBet = true
      actions.bet.betLost(bet)
      emitBetResult({
        betId: bet.id,
        status: 'lost',
        currency: bet.currency
      })
      return
    }

    if (getBetStatusByRaceSettled(bet, newRaceEvent) === betStatusMap.WON) {
      hasWinningBet = true
      actions.player.betWon(bet)
      actions.bet.betWon(bet)
    } else if (getBetStatusByRaceSettled(bet, newRaceEvent) === betStatusMap.LOST) {
      hasLosingBet = true
      actions.bet.betLost(bet)

      // Emit lost bet to parent iframe — there is no server-side bet:lost WS event,
      // losses are only determined client-side from race_settled results
      emitBetResult({
        betId: bet.id,
        status: 'lost',
        currency: bet.currency
      })
    }
  })

  // Clear streak on settlement - calculatePayout is the source of truth
  // When user clicks a betting option, calculatePayout returns accurate streak info
  if (hasWinningBet || hasLosingBet) {
    actions.streak?.clearStreak()
  }

  // A refund above means the server confirmed the bet was never registered (or
  // we couldn't confirm and fell back to purging). Either way the optimistic
  // balance may now be wrong: if the operator HAD debited a stake we just
  // re-credited locally, the in-game balance would sit HIGHER than the real
  // wallet. Unlike every other bet-outcome handler, settlement has no
  // `updateBalance()` call, so the wrong value would stick. Re-read the
  // operator-authoritative balance to reconcile. Harmless when the refund was
  // legitimate (the server agrees the stake was never taken), and a no-op when
  // the channel isn't joined. SOFTSWISS Winbeast/Elitespin/Hiddenjack repro.
  if (didRefundUnacceptedBet) {
    void updateBalance?.()
  }
}
